<template>
    <div>
        <div v-if="tracks && tracks.length" class="listener-track-list">
            <button
                v-for="(item, index) in tracks"
                :key="`${item.id || 'track'}_${index}`"
                :class="[
                    'listener-track-list__row',
                    showImage ? 'has-image' : '',
                    item.id === trackId ? 'is-active' : ''
                ]"
                @click="play(index)"
                @touchstart="handleTouchStart(item, index, $event)"
                @touchmove="handleTouchMove"
                @touchend="handleTouchEnd"
                @touchcancel="handleTouchCancel"
            >
                <div class="listener-track-list__index">
                    <span v-if="item.id === trackId && isPlaying" class="listener-track-list__equalizer">
                        <span></span>
                        <span></span>
                        <span></span>
                    </span>
                    <span v-else>
                        {{ indexField === 'index' ? index + 1 : (item.track || index + 1) }}
                    </span>
                </div>

                <div v-if="showImage" class="listener-track-list__image-wrap">
                    <img :src="item.image || '/static/dist/img/album_null.jpg'" :alt="item.title" class="listener-track-list__image">
                </div>

                <div class="listener-track-list__meta">
                    <div class="listener-track-list__title-row">
                        <div class="listener-track-list__title">{{ item.title }}</div>
                        <span v-if="item.fromFolder === 'webdav'" class="listener-track-list__tag">WebDAV</span>
                    </div>
                    <div class="listener-track-list__sub">
                        <span v-if="!noArtist" class="listener-track-list__sub-text">{{ item.artist || $tt('未知艺术家') }}</span>
                        <span v-if="!noArtist && !noAlbum && item.album" class="listener-track-list__dot">·</span>
                        <span v-if="!noAlbum && item.album" class="listener-track-list__sub-text">{{ item.album }}</span>
                    </div>
                    <div class="listener-track-list__detail">
                        <span>{{ formatDuration(item.duration) }}</span>
                        <span v-if="item.bitrate">{{ item.bitrate }}k</span>
                        <span v-if="item.size">{{ formatSize(item.size) }}</span>
                    </div>
                </div>

                <div class="listener-track-list__tail">
                    <button
                        class="listener-track-list__fav"
                        @click.stop="toggleFavourite(item)"
                    >
                        <svg viewBox="0 0 24 24" :fill="item.favourite ? '#ef4444' : 'none'" stroke="currentColor" stroke-width="2">
                            <path d="M12 21s-6.72-4.35-9.2-8.3A5.38 5.38 0 0 1 7.35 4.6 5.2 5.2 0 0 1 12 7.09 5.2 5.2 0 0 1 16.65 4.6a5.38 5.38 0 0 1 4.55 8.1C18.72 16.65 12 21 12 21z"></path>
                        </svg>
                    </button>
                    <button
                        class="listener-track-list__more"
                        :aria-label="$tt('歌曲操作')"
                        @click.stop="openActionSheet(item, index)"
                    >
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6.75 12a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm6.75 0a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm5.25 1.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z"></path>
                        </svg>
                    </button>
                </div>
            </button>
        </div>

        <div v-else class="listener-track-list__empty">
            <h3>{{ $tt('暂无音乐') }}</h3>
            <p>{{ $tt('还没有找到任何音乐，请稍后再试') }}</p>
        </div>

        <listener-track-action-sheet
            :visible="actionSheetVisible"
            :item="actionItem"
            :index="actionIndex"
            @close="closeActionSheet"
            @open-edit="emitOpenEdit"
            @remove-from-playlist="removeTrackFromPlaylist"
            @delete-track-success="removeTrackFromPlaylist"
        />
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ListenerTrackActionSheet from '@/components/library/mobile/ListenerTrackActionSheet.vue'

export default {
    name: 'ListenerTrackList',
    components: {
        ListenerTrackActionSheet
    },
    props: {
        tracks: { type: Array, required: true },
        noAlbum: { type: Boolean, default: false },
        noArtist: { type: Boolean, default: false },
        indexField: { type: String, default: 'tracknumber' },
        showImage: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
    },
    data() {
        return {
            actionSheetVisible: false,
            actionItem: null,
            actionIndex: -1,
            longPressTimer: null,
            longPressTriggered: false
        }
    },
    computed: {
        ...mapGetters(['trackId']),
        isPlaying() {
            return this.$store.state.common.isPlaying
        }
    },
    beforeDestroy() {
        this.clearLongPressTimer()
    },
    methods: {
        formatDuration(value) {
            if (!isFinite(value)) {
                return '∞'
            }
            const minutes = Math.floor(value / 60)
            const seconds = Math.floor(value % 60)
            return `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
        },
        formatSize(value) {
            return `${(value / 1024 / 1024).toFixed(1)} MB`
        },
        play(index) {
            if (this.readonly) {
                return
            }
            if (this.longPressTriggered) {
                this.longPressTriggered = false
                return
            }
            if (this.tracks[index].id === this.trackId) {
                return this.$store.dispatch('playPause')
            }
            return this.$store.dispatch('playTrackList', {
                index,
                tracks: this.tracks
            })
        },
        openActionSheet(item, index) {
            this.actionItem = item
            this.actionIndex = index
            this.actionSheetVisible = true
        },
        closeActionSheet() {
            this.actionSheetVisible = false
            this.actionItem = null
            this.actionIndex = -1
        },
        toggleFavourite(item) {
            const action = item.favourite ? 'removeFavourite' : 'addFavourite'
            this.$api.subsonic[action](item.id, 'track').then(() => {
                item.favourite = !item.favourite
            })
        },
        emitOpenEdit(item) {
            this.closeActionSheet()
            this.$emit('open-edit', item)
        },
        removeTrackFromPlaylist(index) {
            this.closeActionSheet()
            this.$emit('remove-track', index)
        },
        clearLongPressTimer() {
            if (this.longPressTimer) {
                clearTimeout(this.longPressTimer)
                this.longPressTimer = null
            }
        },
        handleTouchStart(item, index) {
            this.longPressTriggered = false
            this.clearLongPressTimer()
            this.longPressTimer = setTimeout(() => {
                this.longPressTriggered = true
                if (navigator.vibrate) {
                    navigator.vibrate(50)
                }
                this.openActionSheet(item, index)
            }, 450)
        },
        handleTouchMove() {
            this.clearLongPressTimer()
        },
        handleTouchEnd() {
            this.clearLongPressTimer()
        },
        handleTouchCancel() {
            this.clearLongPressTimer()
            this.longPressTriggered = false
        }
    }
}
</script>

<style scoped>
.listener-track-list {
    display: grid;
    gap: 0.75rem;
    padding-bottom: calc(var(--listener-shell-mini-player-height, 4.75rem) + 1.25rem);
}

.listener-track-list__row {
    border: 0;
    padding: 0.8rem;
    border-radius: 1.35rem;
    background: rgba(255, 255, 255, 0.82);
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 0.75rem;
    align-items: center;
    text-align: left;
    box-shadow: 0 14px 28px rgba(148, 163, 184, 0.12);
    transition: background 0.22s ease, box-shadow 0.22s ease, transform 0.22s ease;
}

.listener-track-list__row.has-image {
    grid-template-columns: auto auto minmax(0, 1fr) auto;
}

.listener-track-list__row.is-active {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(241, 245, 249, 0.94));
    box-shadow: 0 18px 36px rgba(148, 163, 184, 0.18);
    transform: translateY(-1px);
}

.listener-track-list__index {
    width: 1.8rem;
    font-size: 0.8rem;
    font-weight: 700;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
}

.listener-track-list__equalizer {
    display: inline-flex;
    align-items: flex-end;
    gap: 0.12rem;
    height: 0.95rem;
}

.listener-track-list__equalizer span {
    width: 0.18rem;
    border-radius: 999px;
    background: #111827;
    animation: listener-track-bounce 0.9s ease-in-out infinite;
}

.listener-track-list__equalizer span:nth-child(1) {
    height: 0.45rem;
}

.listener-track-list__equalizer span:nth-child(2) {
    height: 0.85rem;
    animation-delay: 0.15s;
}

.listener-track-list__equalizer span:nth-child(3) {
    height: 0.62rem;
    animation-delay: 0.3s;
}

.listener-track-list__image-wrap {
    width: 3.2rem;
    height: 3.2rem;
    flex-shrink: 0;
}

.listener-track-list__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 1rem;
}

.listener-track-list__meta {
    min-width: 0;
}

.listener-track-list__title-row,
.listener-track-list__sub,
.listener-track-list__detail {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    min-width: 0;
}

.listener-track-list__title-row {
    width: 100%;
}

.listener-track-list__title {
    flex: 1;
    min-width: 0;
    font-size: 0.92rem;
    font-weight: 700;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-track-list__row.is-active .listener-track-list__title {
    color: #0f172a;
}

.listener-track-list__tag {
    flex-shrink: 0;
    padding: 0.1rem 0.35rem;
    border-radius: 999px;
    background: rgba(255, 156, 0, 0.12);
    color: #b45309;
    font-size: 0.65rem;
    font-weight: 700;
}

.listener-track-list__sub {
    margin-top: 0.28rem;
    font-size: 0.79rem;
    color: #64748b;
}

.listener-track-list__row.is-active .listener-track-list__sub,
.listener-track-list__row.is-active .listener-track-list__detail,
.listener-track-list__row.is-active .listener-track-list__index {
    color: #94a3b8;
}

.listener-track-list__sub-text {
    min-width: 0;
    flex: 1 1 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-track-list__dot {
    flex-shrink: 0;
}

.listener-track-list__detail {
    margin-top: 0.32rem;
    font-size: 0.72rem;
    color: #94a3b8;
    flex-wrap: wrap;
}

.listener-track-list__row.is-active .listener-track-list__image {
    box-shadow: 0 14px 26px rgba(148, 163, 184, 0.18);
}

.listener-track-list__tail {
    display: flex;
    align-items: center;
    justify-self: end;
    flex-shrink: 0;
    gap: 0.35rem;
}

.listener-track-list__fav {
    width: 2.3rem;
    height: 2.3rem;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.9);
    color: #111827;
}

.listener-track-list__row.is-active .listener-track-list__fav {
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 10px 20px rgba(148, 163, 184, 0.16);
}

.listener-track-list__fav svg {
    width: 1rem;
    height: 1rem;
}

.listener-track-list__more {
    width: 2.3rem;
    height: 2.3rem;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.9);
    color: #111827;
    box-shadow: 0 10px 20px rgba(148, 163, 184, 0.1);
    -webkit-tap-highlight-color: transparent;
}

.listener-track-list__row.is-active .listener-track-list__more {
    background: rgba(255, 255, 255, 0.98);
}

.listener-track-list__more svg {
    width: 1rem;
    height: 1rem;
}

.listener-track-list__empty {
    padding: 2rem 1rem;
    text-align: center;
    color: #64748b;
}

.listener-track-list__empty h3 {
    font-size: 1rem;
    font-weight: 700;
    color: #111827;
}

.listener-track-list__empty p {
    margin-top: 0.4rem;
    font-size: 0.82rem;
}

@keyframes listener-track-bounce {
    0%, 100% {
        transform: scaleY(0.85);
        opacity: 0.7;
    }
    50% {
        transform: scaleY(1.15);
        opacity: 1;
    }
}
</style>
